import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, MapPin, Phone, Building, Truck, Building2, Eye, EyeOff, CheckCircle, AlertCircle, Shield, Circle } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import RegistrationModal from '../components/RegistrationModal';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const FIELD_CONFIGS = {
  common: [
    { name: 'firstName', label: 'First Name', type: 'text', icon: User, section: 'personal' },
    { name: 'lastName', label: 'Last Name', type: 'text', icon: User, section: 'personal' },
    { name: 'email', label: 'Email', type: 'email', icon: Mail, section: 'personal' },
    { name: 'phone', label: 'Phone', type: 'tel', icon: Phone, section: 'personal' },
    { name: 'phoneExtension', label: 'Phone Extension', type: 'text', icon: Phone, section: 'personal', optional: true },
    { name: 'companyName', label: 'Company Name', type: 'text', icon: Building, section: 'company' },
    { name: 'password', label: 'Password', type: 'password', icon: Lock, section: 'security' },
    { name: 'confirmPassword', label: 'Confirm Password', type: 'password', icon: Lock, section: 'security' },
  ],
  carrier: [
    { name: 'mcNumber', label: 'MC Number', type: 'text', icon: Truck, section: 'company' },
    { name: 'dotNumber', label: 'DOT Number', type: 'text', icon: Truck, section: 'company' },
    { name: 'scacCode', label: 'SCAC Code', type: 'text', icon: Truck, section: 'company', optional: true },
    { name: 'caatCode', label: 'CAAT Code', type: 'text', icon: Truck, section: 'company', optional: true },
  ]
};

const FORM_SECTIONS = {
  personal: { title: 'Personal Information', icon: User },
  company: { title: 'Company Information', icon: Building },
  security: { title: 'Security', icon: Shield },
  address: { title: 'Address Information', icon: MapPin }
};

const SubmitButton = ({ userType }) => {
  return (
    <button
      type="submit"
      className="w-full flex items-center justify-center px-4 py-3 rounded-lg 
                 bg-indigo-600 hover:bg-indigo-500 
                 text-white font-medium
                 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900"
    >
      {userType === 'customer' ? (
        <>
          <Building2 className="w-4 h-4 mr-2" />
          Register as Customer
        </>
      ) : (
        <>
          <Truck className="w-4 h-4 mr-2" />
          Register as Carrier
        </>
      )}
    </button>
  );
};

const AddressFields = ({ prefix, label }) => (
  <div className="space-y-4">
    <h3 className="text-sm font-medium text-gray-300">{label}</h3>
    <div className="grid grid-cols-1 gap-4">
      <div>
        <label htmlFor={`${prefix}Street`} className="block text-sm font-medium text-gray-300">
          Street Address
        </label>
        <div className="relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MapPin className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            name={`${prefix}Street`}
            required
            className="appearance-none block w-full pl-10 px-3 py-2.5 border border-white/10 rounded-lg shadow-sm bg-white/5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
          />
        </div>
      </div>
      
      <div>
        <label htmlFor={`${prefix}Suite`} className="block text-sm font-medium text-gray-300">
          Suite/Unit (Optional)
        </label>
        <input
          type="text"
          name={`${prefix}Suite`}
          className="appearance-none block w-full px-3 py-2.5 border border-white/10 rounded-lg shadow-sm bg-white/5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${prefix}City`} className="block text-sm font-medium text-gray-300">
            City
          </label>
          <input
            type="text"
            name={`${prefix}City`}
            required
            className="appearance-none block w-full px-3 py-2.5 border border-white/10 rounded-lg shadow-sm bg-white/5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor={`${prefix}State`} className="block text-sm font-medium text-gray-300">
            State
          </label>
          <input
            type="text"
            name={`${prefix}State`}
            required
            className="appearance-none block w-full px-3 py-2.5 border border-white/10 rounded-lg shadow-sm bg-white/5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
          />
        </div>
      </div>
      
      <div>
        <label htmlFor={`${prefix}ZipCode`} className="block text-sm font-medium text-gray-300">
          ZIP Code
        </label>
        <input
          type="text"
          name={`${prefix}ZipCode`}
          required
          className="appearance-none block w-full px-3 py-2.5 border border-white/10 rounded-lg shadow-sm bg-white/5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
        />
      </div>
    </div>
  </div>
);

const renderPasswordRequirements = (validations, formData) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 }}
    className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20"
  >
    <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
      <Shield className="w-4 h-4 text-indigo-400" />
      Password Requirements
    </h3>
    <ul className="space-y-3">
      {[
        { key: 'length', text: 'At least 12 characters' },
        { key: 'uppercase', text: 'One uppercase letter (A-Z)' },
        { key: 'lowercase', text: 'One lowercase letter (a-z)' },
        { key: 'number', text: 'One number (0-9)' },
        { key: 'special', text: 'One special character (!@#$%^&*(),.?":{}|<>)' },
        { key: 'noCommon', text: 'Not a common password' }
      ].map(({ key, text }) => {
        const status = validations[key] ? 'valid' : 'invalid';
        return (
          <motion.li
            key={key}
            className={`flex items-center gap-3 text-sm transition-colors duration-200
              ${status === 'valid' ? 'text-green-300' : 
                status === 'invalid' ? 'text-red-300' : 'text-gray-400'}`}
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: 4 }}
          >
            {status === 'valid' ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : status === 'invalid' ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <Circle className="w-5 h-5 text-gray-500" />
            )}
            <span>{text}</span>
          </motion.li>
        );
      })}
    </ul>

    <div className="mt-6">
      <div className="flex justify-between text-xs mb-2">
        <span className="text-gray-400">Password Strength</span>
        <span className={`font-medium ${
          Object.values(validations).every(Boolean)
            ? 'text-green-400'
            : Object.values(validations).filter(Boolean).length >= 4
            ? 'text-yellow-400'
            : 'text-red-400'
        }`}>
          {Object.values(validations).every(Boolean)
            ? 'Strong'
            : Object.values(validations).filter(Boolean).length >= 4
            ? 'Moderate'
            : 'Weak'}
        </span>
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${
            Object.values(validations).every(Boolean)
              ? 'bg-green-500 w-full'
              : Object.values(validations).filter(Boolean).length >= 4
              ? 'bg-yellow-500 w-2/3'
              : 'bg-red-500 w-1/3'
          }`}
        />
      </div>
    </div>
  </motion.div>
);

const PasswordInput = ({ value, onChange, showPassword, setShowPassword, validations }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="space-y-1">
      <label htmlFor="password" className="block text-sm font-medium text-gray-300">
        Password
      </label>
      <div className="relative rounded-md shadow-sm">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type={showPassword ? 'text' : 'password'}
          name="password"
          id="password"
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          required
          className="appearance-none block w-full pl-10 pr-10 py-2.5 border border-white/10 rounded-lg shadow-sm bg-white/5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
          placeholder="Enter your password"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
        >
          {showPassword ? (
            <EyeOff className="w-5 h-5" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
        </button>
      </div>
      {(isFocused || value) && renderPasswordRequirements(validations, value)}
    </div>
  );
};

const PhoneField = ({ value, onChange }) => {
  // Helper to get default country from phone number
  const getDefaultCountry = (phoneNumber) => {
    if (phoneNumber?.startsWith('+1')) return 'us';
    if (phoneNumber?.startsWith('+')) return 'us';
    return 'us';
  };

  return (
    <div className="space-y-1">
      <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
        Phone
      </label>
      <div className="phone-input-dark">
        <PhoneInput
          country={getDefaultCountry(value)}
          value={value || ''}
          onChange={(phoneValue) => {
            // Ensure the value starts with + for E.164 format
            const formattedNumber = phoneValue.startsWith('+') ? phoneValue : '+' + phoneValue;
            onChange({
              target: {
                name: 'phone',
                value: formattedNumber
              }
            });
          }}
          inputClass="!appearance-none !block !w-full !pl-12 !px-3 !py-2.5 !border !border-white/10 !rounded-lg !shadow-sm !bg-white/5 !text-white !placeholder-gray-400 focus:!outline-none focus:!ring-2 focus:!ring-indigo-500 focus:!border-transparent !text-sm"
          buttonClass="!bg-white/5 !border-white/10 !border-r-0 !rounded-l-lg"
          dropdownClass="!bg-gray-800 !text-white !border-white/10"
          searchClass="!bg-gray-700 !text-white !border-white/10"
          containerClass="w-full"
          enableSearch={true}
          disableSearchIcon={true}
          searchPlaceholder="Search country..."
        />
      </div>
      <p className="text-xs text-gray-400 mt-1">Include country code for international numbers</p>
      <style>{`
        .phone-input-dark .react-tel-input .form-control {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          color: white !important;
        }
        .phone-input-dark .react-tel-input .flag-dropdown {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-right: none !important;
        }
        .phone-input-dark .react-tel-input .selected-flag:hover,
        .phone-input-dark .react-tel-input .selected-flag:focus {
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .phone-input-dark .react-tel-input .country-list {
          background: rgb(31, 41, 55) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        .phone-input-dark .react-tel-input .country-list .country:hover {
          background: rgba(99, 102, 241, 0.3) !important;
        }
        .phone-input-dark .react-tel-input .country-list .country.highlight {
          background: rgba(99, 102, 241, 0.4) !important;
        }
        .phone-input-dark .react-tel-input .country-list .country-name,
        .phone-input-dark .react-tel-input .country-list .dial-code {
          color: white !important;
        }
        .phone-input-dark .react-tel-input .search-box {
          background: rgb(55, 65, 81) !important;
          color: white !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        .phone-input-dark .react-tel-input .search-box::placeholder {
          color: rgba(255, 255, 255, 0.5) !important;
        }
      `}</style>
    </div>
  );
};

const FormField = ({ field, value, onChange, showPassword, setShowPassword, validations }) => {
  if (field.name === 'password') {
    return (
      <PasswordInput
        value={value}
        onChange={onChange}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        validations={validations}
      />
    );
  }

  // Use PhoneInput for phone field
  if (field.name === 'phone') {
    return (
      <PhoneField
        value={value}
        onChange={onChange}
      />
    );
  }

  return (
    <div className="space-y-1">
      <label htmlFor={field.name} className="block text-sm font-medium text-gray-300">
        {field.label}{field.optional && <span className="text-gray-500 ml-1">(Optional)</span>}
      </label>
      <div className="relative rounded-md shadow-sm">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <field.icon className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type={field.type}
          name={field.name}
          id={field.name}
          value={value || ''}
          onChange={onChange}
          required={!field.optional}
          className="appearance-none block w-full pl-10 px-3 py-2.5 border border-white/10 rounded-lg shadow-sm bg-white/5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
        />
      </div>
    </div>
  );
};

const AddressToggle = ({ formData, onToggle }) => (
  <div className="flex items-start mb-6">
    <div className="flex items-center h-5">
      <input
        type="checkbox"
        id="useSameAddress"
        checked={formData.useSameAddress}
        onChange={onToggle}
        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
      />
    </div>
    <div className="ml-3">
      <label htmlFor="useSameAddress" className="text-sm text-gray-300">
        Billing address same as physical address
      </label>
    </div>
  </div>
);

const ConsentDisclosure = ({ value, onChange }) => (
  <div className="space-y-4 bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10">
    <div className="flex items-center gap-2">
      <Shield className="w-5 h-5 text-indigo-400" />
      <h3 className="text-sm font-medium text-white">Consent to Contact</h3>
    </div>
    
    <p className="text-sm text-gray-300 leading-relaxed">
      By checking this box, I acknowledge and agree that Load Link may contact me via email, phone, 
      or text message regarding my account, services, or relevant shipping opportunities. I understand 
      that standard message and data rates may apply, and I can opt-out at any time by updating my 
      preferences in account settings or following the unsubscribe instructions in communications.
    </p>

    <div className="flex items-start pt-2">
      <div className="flex items-center h-5">
        <input
          type="checkbox"
          id="contactConsent"
          checked={value}
          onChange={onChange}
          required
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </div>
      <div className="ml-3">
        <label htmlFor="contactConsent" className="text-sm text-gray-300">
          I agree to receive communications from Load Link
        </label>
      </div>
    </div>
  </div>
);

const CustomerTypeSelector = ({ value, onChange }) => {
  const options = [
    {
      id: 'standard',
      title: 'Standard Customer',
      description: 'Post loads and manage your own freight operations',
      icon: Building2
    },
    {
      id: 'broker',
      title: 'Freight Broker',
      description: 'You operate as a freight broker yourself',
      icon: Truck
    },
    {
      id: 'view_only',
      title: 'Platform Managed',
      description: 'Load Link acts as your freight broker',
      icon: Shield
    }
  ];

  return (
    <div className="col-span-full space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-5 h-5 text-indigo-400" />
        <h3 className="text-sm font-medium text-white">Customer Type</h3>
      </div>
      
      <div className="space-y-3">
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={`w-full p-4 rounded-lg border transition-all duration-200 text-left ${
                value === option.id
                  ? 'border-indigo-500 bg-indigo-500/20 ring-2 ring-indigo-500/30'
                  : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  value === option.id ? 'bg-indigo-500' : 'bg-white/10'
                }`}>
                  <Icon className={`w-4 h-4 ${
                    value === option.id ? 'text-white' : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <h4 className={`text-sm font-medium ${
                    value === option.id ? 'text-white' : 'text-gray-300'
                  }`}>
                    {option.title}
                  </h4>
                  <p className={`text-xs mt-1 ${
                    value === option.id ? 'text-indigo-200' : 'text-gray-400'
                  }`}>
                    {option.description}
                  </p>
                </div>
                {value === option.id && (
                  <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Register = () => {
  const [userType, setUserType] = useState('customer');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    phone: '',
    phoneExtension: '',
    mcNumber: '',
    dotNumber: '',
    scacCode: '',
    caatCode: '',
    physicalAddress: {
      street: '',
      suite: '',
      city: '',
      state: '',
      zipCode: ''
    },
    billingAddress: {
      street: '',
      suite: '',
      city: '',
      state: '',
      zipCode: ''
    },
    useSameAddress: true,
    contactConsent: false,
    is_broker: false,
    customer_type: 'standard',
  });
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [validations, setValidations] = useState({
    length: false,
    number: false,
    special: false,
    uppercase: false,
    lowercase: false,
    noCommon: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const commonPatterns = [
      'password', '123456', 'qwerty', 'admin', 'letmein', 
      'welcome', 'monkey', 'football', 'abc123'
    ];

    const password = formData.password || '';

    setValidations({
      length: password.length >= 12,
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      noCommon: password.length > 0 && !commonPatterns.some(pattern => 
        password.toLowerCase().includes(pattern)
      )
    });
  }, [formData.password]);

  const getRequirementStatus = (key) => {
    if (!formData.password) return 'neutral';
    return validations[key] ? 'valid' : 'invalid';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('physicalAddress.') || name.startsWith('billingAddress.')) {
      const [addressType, field] = name.split('.');
      setFormData(prevData => ({
        ...prevData,
        [addressType]: {
          ...prevData[addressType] || {},
          [field]: value
        }
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: value
      }));
    }
    
    if (error) setError('');
  };

  const handleUseSameAddress = (e) => {
    const checked = e.target.checked;
    setFormData(prevData => ({
      ...prevData,
      useSameAddress: checked,
      ...(checked && {
        billingAddress: { ...prevData.physicalAddress }
      })
    }));
  };

  const handleCloseModal = () => {
    setShowModal(false);
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contactConsent) {
      setError('Please acknowledge the consent disclosure to continue');
      return;
    }

    if (!Object.values(validations).every(Boolean)) {
      setError('Please meet all password requirements');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    // Construct physical address from form data
    const physicalAddress = {
      street: formData.physicalAddress?.street || '',
      suite: formData.physicalAddress?.suite || '',
      city: formData.physicalAddress?.city || '',
      state: formData.physicalAddress?.state || '',
      zipCode: formData.physicalAddress?.zipCode || ''
    };

    // Prepare data for submission
    const submitData = {
      ...formData,
      physicalAddress,
      billingAddress: formData.useSameAddress ? physicalAddress : formData.billingAddress || physicalAddress
    };

    try {
      await api.post(`/register/${userType}`, submitData);
      setShowModal(true);
    } catch (error) {
      console.error('Registration error:', error.response?.data || error);
      setError(error.response?.data?.message || 'An error occurred during registration');
    }
  };

  const commonFields = FIELD_CONFIGS.common;
  const carrierFields = FIELD_CONFIGS.carrier;

  const inputFields = userType === 'carrier' 
    ? [...commonFields, ...carrierFields]
    : commonFields;

  const handleUserTypeChange = (newType) => {
    requestAnimationFrame(() => {
      setUserType(newType);
    });
  };

  const passwordField = inputFields.find(field => field.name === 'password');
  if (passwordField) {
    const passwordIndex = inputFields.indexOf(passwordField);
    inputFields[passwordIndex] = {
      ...passwordField,
      render: () => (
        <PasswordInput
          value={formData.password}
          onChange={handleChange}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          validations={validations}
        />
      )
    };
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-indigo-900 relative">
      {/* Background patterns remain the same */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,_rgba(99,102,241,0.08),transparent_50%)]" />
      </div>

      {/* Updated header section with proper spacing */}
      <div className="relative z-10 w-full bg-gradient-to-b from-gray-900/95 to-transparent backdrop-blur-sm pt-24 pb-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            className="text-4xl font-extrabold text-white tracking-tight text-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Create Account
          </motion.h2>
          <motion.p
            className="text-gray-400 text-sm text-center mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Join our platform to start shipping or carrying loads
          </motion.p>
        </div>
      </div>

      {/* Updated content area with improved scrolling - horizontal scroll prevented */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="bg-white/5 backdrop-blur-xl p-8 lg:p-10 shadow-xl rounded-xl border border-white/10 overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Updated Toggle with Correct Positioning */}
            <div className="mb-10 flex justify-center">
              <div className="flex items-center justify-center bg-white/5 rounded-full p-1 w-[320px] relative">
                <motion.div 
                  className="absolute bg-indigo-600 rounded-full"
                  initial={false}
                  animate={{
                    left: userType === 'customer' ? '0%' : '50%',
                  }}
                  style={{
                    width: '50%',
                    height: '85%',
                    top: '7.5%',
                  }}
                  transition={{ 
                    type: "tween",
                    duration: 0.2,
                  }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUserTypeChange('customer');
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUserTypeChange('customer');
                  }}
                  className={`relative z-10 flex items-center justify-center w-1/2 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    userType === 'customer'
                      ? 'text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Customer
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUserTypeChange('carrier');
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUserTypeChange('carrier');
                  }}
                  className={`relative z-10 flex items-center justify-center w-1/2 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    userType === 'carrier'
                      ? 'text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Carrier
                </button>
              </div>
            </div>

            {/* Updated scrollable form section - removed negative margin and adjusted padding */}
            <div className="overflow-y-auto overflow-x-hidden max-h-[calc(100vh-20rem)] custom-scrollbar">
              <form className="space-y-8" onSubmit={handleSubmit}>
                {Object.entries(FORM_SECTIONS).map(([sectionKey, section]) => {
                  const sectionFields = [
                    ...FIELD_CONFIGS.common.filter(field => field.section === sectionKey),
                    ...(userType === 'carrier' ? FIELD_CONFIGS.carrier.filter(field => field.section === sectionKey) : [])
                  ];

                  if (sectionFields.length === 0 && sectionKey !== 'address') return null;

                  return (
                    <div key={sectionKey} className="space-y-6">
                      <div className="flex items-center gap-2 pb-4 border-b border-white/10">
                        <section.icon className="w-5 h-5 text-indigo-400" />
                        <h3 className="text-lg font-medium text-white">{section.title}</h3>
                      </div>

                      {sectionKey === 'address' ? (
                        <div className="space-y-6">
                          <AddressFields prefix="physicalAddress." label="Physical Address" />
                          <AddressToggle 
                            formData={formData} 
                            onToggle={handleUseSameAddress}
                          />
                          {!formData.useSameAddress && (
                            <AddressFields prefix="billingAddress." label="Billing Address" />
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {sectionFields.map(field => (
                            <FormField
                              key={field.name}
                              field={field}
                              value={formData[field.name]}
                              onChange={handleChange}
                              showPassword={showPassword}
                              setShowPassword={setShowPassword}
                              validations={validations}
                            />
                          ))}
                          {sectionKey === 'company' && userType === 'customer' && (
                            <CustomerTypeSelector
                              value={formData.customer_type}
                              onChange={(value) => {
                                // Update both customer_type and is_broker for backward compatibility
                                setFormData(prev => ({
                                  ...prev,
                                  customer_type: value,
                                  is_broker: value === 'broker'
                                }));
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                <ConsentDisclosure
                  value={formData.contactConsent}
                  onChange={(e) => handleChange({
                    target: {
                      name: 'contactConsent',
                      value: e.target.checked
                    }
                  })}
                />
                
                {error && (
                  <motion.div 
                    className="text-red-400 text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    {error}
                  </motion.div>
                )}
                
                <div className="sticky bottom-0 pt-6 pb-2">
                  <SubmitButton userType={userType} />
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      <RegistrationModal
        isOpen={showModal}
        onClose={handleCloseModal}
        userType={userType}
      />
    </div>
  );
};

export default Register;